/**
 * «Xem từng người» ở thẻ «Tình hình kinh doanh» (CEO 1a · 2a · 3b · 4a, 31/10/2026): mọi
 * người gom theo tiểu đội, đầu nhóm là dòng tổng tiểu đội; mỗi người đủ 7 số + so tháng
 * trước + % góp vào tiểu đội + hạng trong tiểu đội (cao nhất tô xanh, thấp nhất tô đỏ).
 * Điện thoại: thẻ từng người với 3 số chính, bấm tên mở khung chi tiết như cũ.
 */
import { formatIntVN, formatVND, formatVNDCompact } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Badge } from "@/ui/badge";
import type { SalesTeamOverviewMetrics, SalesTeamOverviewNode } from "../api";
import { averageOrder, deltaPercent } from "../overview";
import { PEOPLE_SORT_LABEL, type PeopleSortKey, type PersonRow, type SquadGroup } from "../overviewPeople";
import { PersonMark, SquadMark } from "./marks";

type Props = {
  groups: SquadGroup[];
  sortKey: PeopleSortKey;
  onSort: (k: PeopleSortKey) => void;
  current: boolean;
  onPerson: (n: SalesTeamOverviewNode) => void;
};

function Delta({ v }: { v: number | null }) {
  if (v == null) return <span className="text-muted">—</span>;
  if (v === 0) return <span className="tabular-nums text-muted">0%</span>;
  return (
    <span className={cn("whitespace-nowrap tabular-nums", v > 0 ? "text-success-text" : "text-danger-text")}>
      {v > 0 ? "▲" : "▼"} {Math.abs(v)}%
    </span>
  );
}

function RankBadge({ r }: { r: PersonRow }) {
  if (r.of < 2) return null;
  return (
    <span
      className={cn(
        "whitespace-nowrap rounded px-1.5 py-0.5 text-xs tabular-nums",
        r.top ? "bg-success-bg text-success-text" : r.bottom ? "bg-danger-bg text-danger-text" : "bg-muted/40 text-muted",
      )}
      title={`Hạng ${r.rank} trong ${r.of} ${r.kind === "cskh" ? "CSKH" : "Sales"} của tiểu đội`}
    >
      Hạng {r.rank}/{r.of}
    </span>
  );
}

function Share({ v }: { v: number | null }) {
  if (v == null) return <span className="text-muted">—</span>;
  return (
    <span className="inline-flex items-center justify-end gap-2 tabular-nums">
      <span aria-hidden className="h-1.5 rounded-full bg-primary/40" style={{ width: `${Math.min(v, 100) * 0.4}px` }} />
      {v}%
    </span>
  );
}

function PersonName({ r, onPerson, nowrap = false }: { r: PersonRow; onPerson: (n: SalesTeamOverviewNode) => void; nowrap?: boolean }) {
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-x-2 gap-y-1", nowrap ? "whitespace-nowrap" : "flex-wrap")}>
      <button
        type="button"
        className="inline-flex min-h-11 items-center text-left font-medium underline-offset-2 hover:underline"
        onClick={() => onPerson(r.node)}
      >
        <PersonMark staffId={r.node.id} name={r.node.name} size={24} />
      </button>
      {r.kind === "cskh" ? <Badge variant="outline">CSKH</Badge> : null}
      <RankBadge r={r} />
    </span>
  );
}

const MONEY: { key: keyof SalesTeamOverviewMetrics; label: string }[] = [
  { key: "realRevenue", label: "Thực tế" },
  { key: "expected", label: "Dự kiến" },
];

function Money({ v }: { v: number }) {
  // Bảng nhiều cột ⇒ số gọn ("52 tr"); đưa chuột vào xem đủ số.
  return (
    <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums" title={formatVND(v)}>
      {formatVNDCompact(v)}
    </td>
  );
}

function Cells({ m, current }: { m: SalesTeamOverviewMetrics; current: boolean }) {
  const avg = averageOrder(m);
  return (
    <>
      {MONEY.map((c) => (
        <Money key={c.key} v={m[c.key] as number} />
      ))}
      <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums">{formatIntVN(m.orders)}</td>
      {avg == null ? <td className="px-2 py-1 text-right text-muted">—</td> : <Money v={avg} />}
      <Money v={m.collected} />
      <Money v={m.upsell} />
      {current ? <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums">{formatIntVN(m.overdueLeads ?? 0)}</td> : null}
    </>
  );
}

export function OverviewPeople({ groups, sortKey, onSort, current, onPerson }: Props) {
  const total = groups.reduce((s, g) => s + g.people.length, 0);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <label htmlFor="sap-theo" className="text-muted">
          Sắp và xếp hạng theo
        </label>
        <select
          id="sap-theo"
          className="min-h-11 rounded-md border border-border bg-surface px-2"
          value={sortKey}
          onChange={(e) => onSort(e.target.value as PeopleSortKey)}
        >
          {(Object.keys(PEOPLE_SORT_LABEL) as PeopleSortKey[]).map((k) => (
            <option key={k} value={k}>
              {PEOPLE_SORT_LABEL[k]}
            </option>
          ))}
        </select>
        <span className="text-xs text-muted">
          {groups.length} tiểu đội · {total} người. Hạng và cột «So tháng trước» theo cột đang chọn, tính trong từng tiểu đội: Sales so với Sales, CSKH so với CSKH.
        </span>
      </div>

      {groups.length === 0 ? <p className="text-sm text-muted">Chưa có người nào trong phạm vi đang xem.</p> : null}

      {/* Máy tính: một bảng, mỗi tiểu đội một nhóm có dòng tổng. */}
      <div className="hidden overflow-x-auto rounded-md border border-border bg-surface md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="whitespace-nowrap border-b border-border text-xs text-muted">
              <th className="px-2 py-2 text-left font-medium">Người</th>
              <th className="px-2 py-2 text-right font-medium">Thực tế</th>
              <th className="px-2 py-2 text-right font-medium">Dự kiến</th>
              <th className="px-2 py-2 text-right font-medium">Đơn</th>
              <th className="px-2 py-2 text-right font-medium">TB một đơn</th>
              <th className="px-2 py-2 text-right font-medium">Đã đưa</th>
              <th className="px-2 py-2 text-right font-medium">Bán thêm</th>
              {current ? <th className="px-2 py-2 text-right font-medium">Quá hẹn</th> : null}
              <th className="px-2 py-2 text-right font-medium" title={`So tháng trước, theo cột «${PEOPLE_SORT_LABEL[sortKey]}»`}>
                So tháng trước
              </th>
              <th className="px-2 py-2 text-right font-medium">Góp vào tiểu đội</th>
            </tr>
          </thead>
          {groups.map((g) => (
            <tbody key={g.squad.id} data-testid={`nhom-${g.squad.id}`}>
              <tr className="border-b border-border bg-muted/30 font-medium">
                <td className="px-2 py-1">
                  <SquadMark squadId={g.squad.id} name={g.squad.name} size={24}>
                    {g.squad.name} <span className="text-xs font-normal text-muted">· {g.people.length} người</span>
                  </SquadMark>
                </td>
                <Cells m={g.squad.metrics} current={current} />
                <td className="px-2 py-1 text-right">
                  <Delta v={deltaPercent(g.squad.metrics[sortKey], g.squad.prev?.[sortKey])} />
                </td>
                <td className="px-2 py-1 text-right text-muted">—</td>
              </tr>
              {g.people.map((r) => (
                <tr key={r.node.id} className="border-b border-border last:border-b-0" data-testid={`nguoi-${r.node.id}`}>
                  <td className="py-1 pl-9 pr-2">
                    <PersonName r={r} onPerson={onPerson} nowrap />
                  </td>
                  <Cells m={r.node.metrics} current={current} />
                  <td className="px-2 py-1 text-right">
                    <Delta v={r.delta} />
                  </td>
                  <td className="px-2 py-1 text-right">
                    <Share v={r.share} />
                  </td>
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>

      {/* Điện thoại: 3 số chính (2a), bấm tên mở khung chi tiết đủ số. */}
      <div className="space-y-3 md:hidden">
        {groups.map((g) => (
          <section key={g.squad.id} className="rounded-md border border-border bg-surface">
            <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/30 p-3 text-sm font-medium">
              <SquadMark squadId={g.squad.id} name={g.squad.name} size={24} />
              <span className="tabular-nums">{formatVND(g.squad.metrics.realRevenue)}</span>
            </header>
            <ul className="divide-y divide-border">
              {g.people.map((r) => (
                <li key={r.node.id} className="space-y-1 p-3 pl-6 text-sm">
                  <PersonName r={r} onPerson={onPerson} />
                  <dl className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <dt className="text-muted">Thực tế</dt>
                      <dd className="tabular-nums">{formatVND(r.node.metrics.realRevenue)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted">Đơn</dt>
                      <dd className="tabular-nums">{formatIntVN(r.node.metrics.orders)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted">Bán thêm</dt>
                      <dd className="tabular-nums">{formatVND(r.node.metrics.upsell)}</dd>
                    </div>
                  </dl>
                  <p className="flex flex-wrap gap-x-3 text-xs text-muted">
                    <span>
                      So tháng trước: <Delta v={r.delta} />
                    </span>
                    <span>Góp {r.share == null ? "—" : `${r.share}%`} tiểu đội</span>
                  </p>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
